import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useLabelsVisibility } from "core/hooks/useLabelsVisibility";
import Tooltip from "@mui/material/Tooltip";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StarIcon from "@mui/icons-material/Star";
import FlagIcon from "@mui/icons-material/Flag";
import SportsFootballIcon from "@mui/icons-material/SportsFootball";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SportsVolleyballIcon from "@mui/icons-material/SportsVolleyball";
import SportsGolfIcon from "@mui/icons-material/SportsGolf";
import SportsHandballIcon from "@mui/icons-material/SportsHandball";
import SportsRugbyIcon from "@mui/icons-material/SportsRugby";
import SportsMmaIcon from "@mui/icons-material/SportsMma";
import SportsKabaddiIcon from "@mui/icons-material/SportsKabaddi";
import SportsMotorsportsIcon from "@mui/icons-material/SportsMotorsports";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import PoolIcon from "@mui/icons-material/Pool";
import DownhillSkiingIcon from "@mui/icons-material/DownhillSkiing";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SportsIcon from "@mui/icons-material/Sports";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimerIcon from "@mui/icons-material/Timer";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CelebrationIcon from "@mui/icons-material/Celebration";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

// Новые иконки (только те, которые точно существуют)
import MovieIcon from "@mui/icons-material/Movie";
import VideocamIcon from "@mui/icons-material/Videocam";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import MicIcon from "@mui/icons-material/Mic";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import SpeedIcon from "@mui/icons-material/Speed";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import BoltIcon from "@mui/icons-material/Bolt";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import InsightsIcon from "@mui/icons-material/Insights";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";
import { SvgIcon } from "@mui/material";

// Кастомные SVG иконки только для карточек
const RedCardIcon = (props) => (
  <SvgIcon {...props}>
    <rect
      x="6"
      y="3"
      width="12"
      height="18"
      rx="2"
      ry="2"
      fill="#DC2626"
      stroke="#B91C1C"
      strokeWidth="0.5"
    />
  </SvgIcon>
);

const YellowCardIcon = (props) => (
  <SvgIcon {...props}>
    <rect
      x="6"
      y="3"
      width="12"
      height="18"
      rx="2"
      ry="2"
      fill="#FBBF24"
      stroke="#F59E0B"
      strokeWidth="0.5"
    />
  </SvgIcon>
);

const PREFIX = "TimecodeMarkers";

const classes = {
  container: `${PREFIX}-container`,
  marker: `${PREFIX}-marker`,
  icon: `${PREFIX}-icon`,
  arrow: `${PREFIX}-arrow`,
};

const Root = styled("div")(({ theme }) => ({
  [`&.${classes.container}`]: {
    position: "absolute",
    top: -15,
    left: 0,
    right: 0,
    height: 24,
    pointerEvents: "none",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.marker}`]: {
    position: "absolute",
    cursor: "pointer",
    pointerEvents: "auto",
    transition: "all 0.2s ease",
    transform: "translate(-50%, -50%)",
    top: "50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    opacity: 0.6,
    "&:hover": {
      opacity: 1,
      transform: "translate(-50%, -50%) scale(1.1)",
      [`& .${classes.icon}`]: {
        backgroundColor: "#f8f8ff",
        boxShadow: `0 2px 12px rgba(0,0,0,0.2), 0 0 8px ${theme.palette.primary.main}40`,
      },
      [`& .${classes.icon} svg`]: {
        color: theme.palette.primary.main,
      },
    },
  },

  [`& .${classes.icon}`]: {
    width: 18,
    height: 18,
    backgroundColor: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    marginBottom: 2,
    "& svg": {
      fontSize: 14,
      color: "#666666",
      transition: "color 0.2s ease",
    },
  },

  [`& .${classes.arrow}`]: {
    width: 0,
    height: 0,
    borderLeft: "3px solid transparent",
    borderRight: "3px solid transparent",
    borderTop: "3px solid white",
    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
  },
}));

// Icon map for timecode types (соответствует TimecodeIcon из core/schema.graphql)
export const ICON_MAP = {
  // Общие иконки
  PLAY_ARROW: PlayArrowIcon,
  STAR: StarIcon,
  FLAG: FlagIcon,
  TIMER: TimerIcon,
  TROPHY: EmojiEventsIcon,
  FIRE: WhatshotIcon,
  LIGHTNING: FlashOnIcon,
  BOLT: BoltIcon,
  AWESOME: AutoAwesomeIcon,
  CELEBRATION: CelebrationIcon,
  TRENDING: TrendingUpIcon,
  SPORTS: SportsIcon,

  // Арбитраж и карточки
  WHISTLE: SportsIcon,
  RED_CARD: RedCardIcon,
  YELLOW_CARD: YellowCardIcon,

  // Видео и контент
  MOVIE: MovieIcon,
  VIDEOCAM: VideocamIcon,
  CAMERA: CameraAltIcon,
  MIC: MicIcon,
  PLAY_CIRCLE: PlayCircleIcon,
  REPLAY: ReplayIcon,
  SLOW_MOTION: SlowMotionVideoIcon,
  SPEED: SpeedIcon,
  VOLUME_UP: VolumeUpIcon,
  FULLSCREEN: FullscreenIcon,
  RECORD_VOICE: RecordVoiceOverIcon,

  // Эмоциональные реакции
  HEART: FavoriteIcon,
  THUMB_UP: ThumbUpIcon,
  THUMB_DOWN: ThumbDownIcon,
  EMOTIONS: EmojiEmotionsIcon,
  VERY_SATISFIED: SentimentVerySatisfiedIcon,
  HOT_FIRE: LocalFireDepartmentIcon,
  INSIGHTS: InsightsIcon,

  // Командные виды спорта
  FOOTBALL: SportsFootballIcon,
  BASKETBALL: SportsBasketballIcon,
  SOCCER: SportsSoccerIcon,
  HOCKEY: SportsHockeyIcon,
  VOLLEYBALL: SportsVolleyballIcon,
  BASEBALL: SportsBaseballIcon,
  RUGBY: SportsRugbyIcon,
  HANDBALL: SportsHandballIcon,
  CRICKET: SportsIcon, // Используем общую спортивную иконку

  // Индивидуальные виды спорта
  TENNIS: SportsTennisIcon,
  GOLF: SportsGolfIcon,
  RUNNING: DirectionsRunIcon,
  SWIMMING: PoolIcon,
  SKIING: DownhillSkiingIcon,
  FITNESS: FitnessCenterIcon,
  GYMNASTICS: SportsIcon, // Используем общую спортивную иконку

  // Боевые искусства
  MMA: SportsMmaIcon,
  MARTIAL_ARTS: SportsMmaIcon, // Используем MMA иконку
  KABADDI: SportsKabaddiIcon,

  // Автоспорт и киберспорт
  MOTORSPORTS: SportsMotorsportsIcon,
  ESPORTS: SportsEsportsIcon,

  // Спортивные дополнения
  SPORTS_SCORE: SportsIcon, // Используем общую спортивную иконку
  SPORTS_BAR: SportsIcon, // Используем общую спортивную иконку
};

function TimecodeMarkers({ timecodes, duration, onTimecodeClick }) {
  const [labelsVisible] = useLabelsVisibility();

  if (!duration || !timecodes || timecodes.length === 0 || !labelsVisible) {
    return null;
  }

  // Фильтруем таймкоды:
  // 1. Только MARKER типы (аннотации отображаются отдельно)
  // 2. Не выходят за границы видео
  const timecodesToRender = timecodes.filter(
    (tc) => tc.time <= duration && (!tc.type || tc.type === "MARKER")
  );

  return (
    <Root className={classes.container}>
      {timecodesToRender.map((timecode, index) => {
        const leftPosition = (timecode.time / duration) * 100;

        return (
          <Tooltip
            key={index}
            title={
              <div>
                <div style={{ fontWeight: "bold" }}>{timecode.title}</div>
                <div style={{ fontSize: "0.8em" }}>{timecode.description}</div>
                <div style={{ fontSize: "0.7em", opacity: 0.8 }}>
                  {Math.floor(timecode.time / 60)}:
                  {String(Math.floor(timecode.time % 60)).padStart(2, "0")}
                </div>
              </div>
            }
            placement="top"
            arrow
            PopperProps={{
              style: { zIndex: 9999 },
            }}
          >
            <div
              className={classes.marker}
              style={{ left: `${leftPosition}%` }}
              onClick={() => onTimecodeClick(timecode.time)}
            >
              <div className={classes.icon}>
                {(() => {
                  const IconComponent = ICON_MAP[timecode.icon] || FlagIcon;
                  return <IconComponent />;
                })()}
              </div>
              <div className={classes.arrow} />
            </div>
          </Tooltip>
        );
      })}
    </Root>
  );
}

TimecodeMarkers.propTypes = {
  timecodes: PropTypes.array,
  duration: PropTypes.number.isRequired,
  onTimecodeClick: PropTypes.func.isRequired,
};

export default TimecodeMarkers;
